import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAppOnlineStatus } from '../../../Slice';
import { DefaultChannel, ExploreChannelItem, SearchBox, SmartListView } from '../../components';
import { ImageCreateChannel, loaderGIF } from '../../rootAccess';
import { handleChannelNavigation, useExploreList } from '../../utils';
import { setActiveCategoryId } from '../../../Slice/explorechannel';

function ExploreChannels({ isInModal = false }) {
   const loadingRef = useRef(null);
   const isOnline = useSelector(getAppOnlineStatus);
   const { channelList, hasMore, loadMore, loading, updateSearchText, searching, searchText, reset } = useExploreList(loadingRef);

   const prevIsOnline = useRef(isOnline);
   const dispatch = useDispatch();


   useEffect(() => {
      if (!prevIsOnline.current && isOnline) {
         reset();
      }
      prevIsOnline.current = isOnline;
   }, [isOnline, reset]);

   const renderEmptyView = useMemo(() => {
      const isSearching = !!searchText;

      if (!isOnline) {
         return (
            <DefaultChannel
               Title="No internet connection"
               Description=""
               btnText=""
               btnClick={undefined}
               Image=""
               alt=""
            />
         );
      }

      const isSearchingResult = isSearching;
      const shouldShowDefault = !isSearchingResult;

      return (
         <DefaultChannel
            Title={isSearchingResult ? 'No results found' : 'Engage with your People'}
            Description={
               shouldShowDefault
                  ? 'The community feature connects users for interaction and collaboration, fostering engagement through discussions and updates.'
                  : ''
            }
            btnText={shouldShowDefault ? 'Create Channel' : ''}
            btnClick={shouldShowDefault ? () => handleChannelNavigation({ pane: 'createChannel' }) : undefined}
            Image={shouldShowDefault ? ImageCreateChannel : ''}
            alt={shouldShowDefault ? 'Create Channel Icon' : ''}
         />
      );
   }, [isOnline, searchText]);

   const renderLoader = useCallback(
      () => (
         <div style={{ textAlign: 'center' }} ref={loadingRef}>
            <img src={loaderGIF} alt="Loading" style={{ width: '40px', height: '40px' }} />
         </div>
      ),
      [],
   );


   const handleSeeAll = (category) => {
      dispatch(setActiveCategoryId(category));
      handleChannelNavigation({ pane: 'categoryChannels' });
   };

   // render one category block (item is category block from API)
   const renderCategoryBlock = useCallback(
      ({ item: catBlock }) => {
         const cat = catBlock.category || {};
         const channels = Array.isArray(catBlock.channels) ? catBlock.channels : [];
         // show up to 4 channels
         const display = channels.slice(0, 4);
         const showSeeAll = Number(cat.totalChannels) > 4;

         return (
            <section key={cat.id} className="explore-category">
               <div className="explore-category-header">
                  <h3 className="explore-category-title">{cat.category}</h3>
                  {showSeeAll && (
                     <button
                        type="button"
                        className="see-all-categories"
                        onClick={() => handleSeeAll(cat)}>
                        See all
                     </button>
                  )}
               </div>

               <div className="explore-channels-grid">
                  {display.map((ch) => (
                     <ExploreChannelItem key={ch.channelId || ch.id} item={ch} searchText={searchText} />
                  ))}
               </div>
            </section>
         );
      },
      [searchText],
   );

   const sections = useMemo(() => {
      return [
         {
            title: 'Explore Channels',
            data: Array.isArray(channelList) ? channelList : [],
            renderItem: renderCategoryBlock,
            numColumns: 1,
         },
      ];
   }, [channelList, renderCategoryBlock]);

   return (
      <>
         <SearchBox onChange={updateSearchText} autoFocus />
         <div
            className="channels-list-container"
            style={{
               ...(isInModal && { maxHeight: '80vh', paddingBottom: '7em' }),
            }}>
            <SmartListView
               sections={sections}
               ListEmptyComponent={!loading && renderEmptyView}
               onLoadBottom={loadMore}
               hasMoreBottom={hasMore}
               ListFooterComponent={loading ? renderLoader() : null}
               ListOverlayComponent={searching ? renderLoader() : null}
            />
         </div>
      </>
   );
}

ExploreChannels.propTypes = {
   isInModal: PropTypes.bool,
};

export default ExploreChannels;